import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { action, API, controller } from "../api/api";
import { PROD_TYPE_SERVICE } from "../utils/cartLine";

// "Would this cart fit the shelf?" — asked BEFORE a line is added or raised, when the
// branch disallows negative stock (Outlet → Settings). The answer comes from the
// server (Sell/CheckStock), which runs the same rule the posting enforces: base units,
// free units, composites exploded into components, variants by variant. The client
// never re-derives stock — the figures it has are wrong for scans, variants and
// composites, and a second version of the rule would drift from the first.
//
// A pass is advice, not a reservation: the posting re-checks under a row lock and
// stays the authority (two tills racing for the last unit are settled there).

// The whole projected cart in the wire shape. Whole, not just the touched product: a
// composite draws on components another line may also draw on.
export const toStockCheckLines = (sellItems) =>
  (sellItems || [])
    .filter((l) => l && l.prodId && l.prodType !== PROD_TYPE_SERVICE)
    .map((l) => ({
      prodId: l.prodId,
      varProdId: l.varProdId || null,
      qty: Math.max(0, parseFloat(l.qty) || 0),
      freeQty: Math.max(0, parseFloat(l.freeQty) || 0),
      unitId: l.unitId || null,
    }))
    .filter((l) => l.qty > 0 || l.freeQty > 0);

const sameLine = (ref, line) =>
  ref.prodId === line?.prodId && (ref.varProdId || null) === (line?.varProdId || null);

// Does a shortage involve this cart line? (For a composite the shortage is a component,
// and `lines` names the composite's line.)
export const shortageTouches = (shortage, line) =>
  (shortage?.lines || []).some((ref) => sameLine(ref, line));

// The alert every screen shows on a refusal — one wording everywhere.
export const stockRefusedAlert = (t, message) => ({
  isOpen: true,
  type: "error",
  title: t("common:notEnoughStock"),
  subTitle: message,
});

/**
 * @param outletId       the branch the sale will post to
 * @param allowNegative  the branch's switch when the screen knows it. Only `true`
 *                       skips the request; unknown (undefined) asks the server.
 * @param sellId         a posted sale being edited/continued — its own lines are
 *                       credited back so only what the edit ADDS is judged.
 * @returns check(nextSellItems, changedLine) → { allowed, message, shortages }
 */
const useStockGate = ({ outletId, allowNegative, sellId } = {}) =>
  useCallback(
    async (nextSellItems, changedLine) => {
      const pass = { allowed: true, message: null, shortages: [] };
      // Allowing branches — nearly all of them — never pay a round trip.
      if (allowNegative === true || !outletId) return pass;
      if (changedLine && (!changedLine.prodId || changedLine.prodType === PROD_TYPE_SERVICE))
        return pass;

      const lines = toStockCheckLines(nextSellItems);
      if (lines.length === 0) return pass;

      try {
        const res = await API(controller.Sell + action.CheckStock).post({
          outletId,
          sellId: sellId || null,
          lines,
        });
        const data = res?.status === 200 ? res.result : null;
        // Refused for access or validation: say so rather than pretend it fits.
        if (!data) return { allowed: false, message: res?.msg, shortages: [] };
        if (data.allowed) return pass;

        const shortages = data.shortages || [];
        // An add is refused only for ITS product — a line already short (a loaded
        // park/quote) is marked on its own row and must not block every other add.
        if (changedLine && !shortages.some((s) => shortageTouches(s, changedLine))) return pass;
        return { allowed: false, message: data.message, shortages };
      } catch {
        // Fail open: the axios interceptor already raised the network/server error
        // banner, and the posting still refuses at Pay. A dropped request must never
        // lock a cashier out of selling.
        return pass;
      }
    },
    [outletId, allowNegative, sellId]
  );

/**
 * Marks the lines of a cart that arrived WHOLE (a quote, park, continued or edited
 * sale, a sales order to bill) and that the branch's stock can no longer cover. Never
 * trims a line: it sets `line.stockShort` to a message, and the screen keeps Pay/Save
 * shut while any line carries one. While anything is marked, every quantity / unit
 * change re-asks, so lowering a quantity clears its own mark.
 *
 * @param sellItems    the cart's lines
 * @param setCartData  the cart setter (functional updates; the cart has `sellItems`)
 * @param check        the function useStockGate returned
 * @returns requestRecheck() — call once after loading a whole cart
 */
export const useStockShortMarks = (sellItems, setCartData, check) => {
  const { t } = useTranslation();
  const [recheck, setRecheck] = useState(0);
  const handled = useRef(0);
  const sig = (sellItems || [])
    .map((x) => `${x.prodId}:${x.varProdId}:${x.unitId}:${x.qty}:${x.freeQty}`)
    .join("|");

  useEffect(() => {
    const requested = recheck !== handled.current;
    const anyShort = (sellItems || []).some((x) => x.stockShort);
    if (!requested && !anyShort) return undefined;
    handled.current = recheck;
    let live = true;
    check(sellItems).then((res) => {
      if (!live) return;
      setCartData((c) => ({
        ...c,
        sellItems: (c.sellItems || []).map((x) => {
          const s = res.shortages.find((sh) => shortageTouches(sh, x));
          const stockShort = s
            ? t("common:cartStockShort", {
                // Stock already below zero is shown as none, never "Only -3".
                available: Number(Math.max(0, Number(s.have)).toFixed(3)),
                needed: Number(Number(s.need).toFixed(3)),
              })
            : "";
          return (x.stockShort || "") === stockShort ? x : { ...x, stockShort };
        }),
      }));
    });
    return () => {
      live = false;
    };
    // Keyed on what can change a shortage, not on the flags this effect writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, recheck, check]);

  return useCallback(() => setRecheck((n) => n + 1), []);
};

export default useStockGate;
